/**
 * #76 @mentions notification bell (design.functional §4 Discussion "notification hooks"): left
 * of the user menu, only when `flag.mentions` is on and someone is signed in (TopBar decides
 * that — this component assumes both). Popover list is newest first; a row marks itself read
 * and navigates to the mentioning entity's record on click.
 *
 * A mention never grants access (api/src/comments/mentions.ts): the entity can be gone
 * (`entityId` null) or one the reader may no longer open (`entityName` null even though the
 * entity still exists) — both render as plain text, no link, in their own words.
 */
import { useState } from 'react';
import { Bell } from 'lucide-react';
import {
  type AppNotification,
  notificationBadge,
  useMarkAllNotificationsRead,
  useMarkNotificationsRead,
  useNotifications,
} from '@/api/ideas';
import { Link } from 'react-router-dom';
import { entityHref } from '@/lib/entity-links';
import { Button, Popover, PopoverItem } from '@/kit';
import { formatDateTime } from '@/lib/format';


/** The entity named in words; the whole row is the link (#239), so no link nests inside it. */
function EntityMention({ n }: { n: AppNotification }) {
  if (!n.entityId) return <span className="italic text-ink-muted">a record that no longer exists</span>;
  if (n.entityName === null) return <span className="italic text-ink-muted">a record you can no longer open</span>;
  return <span className="font-medium text-link">{n.entityName || n.entityKey}</span>;
}

/**
 * #239: one row, one control — click, Enter or Space marks it read and opens the record's
 * drawer. A row whose record is gone or no longer readable only marks read (nothing to open).
 */
function Row({ n, onOpen }: { n: AppNotification; onOpen: (n: AppNotification) => void }) {
  const href = n.entityId && n.entityName !== null ? entityHref({ type: n.entityType, id: n.entityId, key: n.entityKey }) : null;
  const rowClass = n.read ? 'k-note-row' : 'k-note-row k-note-unread';
  const body = (
    <>
      <p className="text-sm text-ink">
        <span className="font-medium">{n.actorName}</span> mentioned you on <EntityMention n={n} />
      </p>
      {n.excerpt ? <p className="k-meta mt-0.5 line-clamp-2">{n.excerpt}</p> : null}
      <p className="k-meta mt-0.5">{formatDateTime(n.at)}</p>
    </>
  );
  return (
    <li data-testid="notification-row" className="border-b border-divider last:border-b-0">
      {href ? (
        <Link
          to={href}
          onClick={() => onOpen(n)}
          onKeyDown={(e) => {
            // #296: Space activates a link like Enter (the browser would scroll instead).
            if (e.key === ' ' || e.key === 'Spacebar') {
              e.preventDefault();
              e.currentTarget.click();
            }
          }}
          aria-label={`Open ${n.entityName || n.entityKey} from the notification by ${n.actorName}`}
          className={rowClass}
        >
          {body}
        </Link>
      ) : (
        <PopoverItem onSelect={() => onOpen(n)} aria-label={`Mark read: ${n.actorName} mentioned you`} className={rowClass}>
          {body}
        </PopoverItem>
      )}
    </li>
  );
}

export function NotificationBell() {
  const notifications = useNotifications(true);
  const markRead = useMarkNotificationsRead();

  const items = notifications.data?.items ?? [];
  const unread = notifications.data?.unread ?? 0;
  const markAll = useMarkAllNotificationsRead();
  const badge = notificationBadge(unread);

  const [open, setOpen] = useState(false);
  const openRow = (n: AppNotification) => {
    if (!n.read) markRead.mutate([n.id]);
    // The row's link navigates; the panel closes so the record's drawer is what you see.
    setOpen(false);
  };

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      align="end"
      aria-label="Notifications"
      trigger={
        <Button icon data-testid="notification-bell" aria-label={`Notifications, ${unread} unread`} className="k-bell">
          <Bell aria-hidden />
          {badge ? (
            <span data-testid="notification-count" className="k-badge">
              {badge}
            </span>
          ) : null}
        </Button>
      }
    >
      <div data-testid="notification-panel" className="k-note-panel">
        <div className="k-note-head">
          <span>Notifications</span>
          <Button variant="quiet" data-testid="notification-mark-all" disabled={unread === 0 || markAll.isPending} onClick={() => markAll.mutate()}>
            Mark all read
          </Button>
        </div>
        <div className="k-note-list">
          {notifications.isError ? (
            <p className="k-meta">Notifications could not be loaded.</p>
          ) : notifications.isLoading ? (
            <p className="k-meta">Loading…</p>
          ) : items.length === 0 ? (
            <p data-testid="notification-empty" className="k-meta">
              No notifications yet. When someone @mentions you in a discussion it appears here. Teams delivery
              comes in a later release.
            </p>
          ) : (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {items.map((n) => (
                <Row key={n.id} n={n} onOpen={openRow} />
              ))}
            </ul>
          )}
        </div>
      </div>
    </Popover>
  );
}
